import { useState, useEffect } from 'react';
import exifr from 'exifr';
import { Shield, AlertCircle, MapPin, Camera, Calendar, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

export function ExifScrub() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [exif, setExif] = useState<Record<string, unknown> | null>(null);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);
    // Re-parse EXIF from the (new) first file for the inspection panel
    if (next[0]) {
      try {
        const data = await exifr.parse(next[0], true);
        setExif(data ?? {});
      } catch {
        setExif({});
      }
    }
  }

  async function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setExif(null);
      return;
    }
    if (i === 0) {
      try {
        setExif((await exifr.parse(next[0], true)) ?? {});
      } catch {
        setExif({});
      }
    }
  }

  async function scrubAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const img = await loadImage(url);
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d')!;
          ctx.drawImage(img, 0, 0);
          const isPng = file.type.includes('png');
          const blob = await canvasToBlob(c, isPng ? 'image/png' : 'image/jpeg', 0.95);
          const ext = isPng ? '.png' : '.jpg';
          return { blob, filename: `${stripExt(file.name)}-scrubbed${ext}` };
        },
        {
          zipName: 'scrubbed',
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  const lat = typeof exif?.latitude === 'number' ? (exif.latitude as number) : undefined;
  const lon = typeof exif?.longitude === 'number' ? (exif.longitude as number) : undefined;
  const make = typeof exif?.Make === 'string' ? exif.Make : '';
  const model = typeof exif?.Model === 'string' ? exif.Model : '';
  const taken = exif?.DateTimeOriginal ? String(exif.DateTimeOriginal) : '';
  const hasGPS = !!(lat !== undefined || exif?.GPSLatitude);
  const fields = exif ? Object.entries(exif) : [];
  const sensitive = [
    'latitude',
    'longitude',
    'GPSLatitude',
    'GPSLongitude',
    'GPSDateStamp',
    'CreateDate',
    'DateTimeOriginal',
    'Make',
    'Model',
    'Software',
    'SerialNumber',
  ];

  return (
    <div>
      <ToolHeader
        toolId="exif-scrub"
        title="EXIF inspector & scrubber"
        description="Inspect every metadata tag attached to a photo. Strip all of it for safe sharing. Drop multiple files for bulk."
        badges={
          <>
            {files.length > 1 && <Badge variant="accent">{files.length} files</Badge>}
            {exif &&
              (fields.length ? (
                <Badge variant={hasGPS ? 'danger' : 'warning'}>
                  {hasGPS ? 'contains GPS' : `${fields.length} tags`}
                </Badge>
              ) : (
                <Badge variant="success">no metadata</Badge>
              ))}
          </>
        }
      />

      {files.length === 0 ? (
        <Dropzone
          accept="image/jpeg,image/png,image/heic,image/webp"
          multiple
          onFiles={handleFiles}
          hint="JPEG, PNG, HEIC, WebP — drop one or many"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 !p-0">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium truncate">
                {files[0].name}
                {files.length > 1 && (
                  <span className="text-fg-subtle ml-2 text-xs">· first of {files.length}</span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFiles([]);
                  setExif(null);
                }}
              >
                Clear
              </Button>
            </div>
            {hasGPS && (
              <div className="px-4 py-3 bg-danger/5 border-b border-danger/20 flex items-start gap-2 text-xs">
                <AlertCircle size={14} className="text-danger shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-danger">GPS coordinates present</div>
                  <div className="text-fg-muted mt-0.5">
                    This photo includes the location where it was taken. Strip metadata before sharing.
                  </div>
                </div>
              </div>
            )}
            <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
              {fields.length === 0 ? (
                <div className="p-6 text-sm text-fg-subtle text-center">
                  This file has no readable EXIF data.
                </div>
              ) : (
                fields.map(([k, v]) => {
                  const isSensitive = sensitive.some((s) =>
                    k.toLowerCase().includes(s.toLowerCase()),
                  );
                  return (
                    <div key={k} className="flex items-center gap-3 px-4 py-2">
                      <code className="text-2xs font-mono font-semibold text-fg-muted w-44 shrink-0 truncate">
                        {k}
                      </code>
                      <code className="text-xs font-mono flex-1 truncate">{String(v)}</code>
                      {isSensitive && <Badge variant="warning">sensitive</Badge>}
                    </div>
                  );
                })
              )}
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/heic,image/webp"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
          <Card className="space-y-3">
            <div className="space-y-2 text-sm">
              {make && <Row icon={Camera} label="Camera" value={`${make} ${model}`.trim()} />}
              {taken && <Row icon={Calendar} label="Taken" value={taken} />}
              {hasGPS && (
                <Row
                  icon={MapPin}
                  label="GPS"
                  value={`${lat?.toFixed(5) ?? '?'}, ${lon?.toFixed(5) ?? '?'}`}
                  danger
                />
              )}
            </div>
            <Button variant="primary" className="w-full" loading={working} onClick={scrubAll}>
              <Shield size={11} />
              {files.length > 1 ? `Strip metadata (${files.length})` : 'Strip metadata'}
            </Button>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
            <div className="text-2xs text-fg-subtle leading-relaxed">
              Scrubbing re-encodes the image without any embedded metadata, EXIF, or location data.
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
  danger,
}: {
  icon: typeof MapPin;
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={12} className={danger ? 'text-danger' : 'text-fg-subtle'} />
      <span className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</span>
      <span className={`ml-auto text-xs font-mono ${danger ? 'text-danger' : ''}`}>{value}</span>
    </div>
  );
}

export default ExifScrub;
